import { roundQuantity } from '../schedule/unit-of-measure';
import { isValidOutline } from './polygon';
import { stairFormwork, stairProjection, stairSteps, stairVolume, type Stair } from './stair';

/**
 * O que a conta tira da escada desenhada (Fase 27, etapa 5 — D10, F17, RN-360, RN-367, RN-373).
 *
 * É a irmã de `slab-totals.ts` e `retaining-totals.ts`: as contas de **cada escada** moram em
 * `stair.ts`, o contrato, e esta é a soma delas na tipologia. Nenhuma medida de AMBIENTE, PAREDE,
 * PILAR, VIGA ou das peças anteriores soma o que está aqui (RN-360) — e a escada também não soma
 * o ambiente ESCADA, que continua medindo o acabamento: piso, pintura, corrimão (F17).
 *
 * > **A regra em uma linha (RN-360):** a escada mede o concreto, a fôrma e o escoramento; o
 * > ambiente ESCADA, o acabamento — cada um com a sua conta, e nenhum entra na do outro.
 */

/** O concreto mora na terceira casa, como o do bloco: duas comeriam o grama do aço dele. */
export const STAIR_VOLUME_DECIMALS = 3;

/** Fôrma e projeção: duas casas, como a área do ambiente. */
export const STAIR_AREA_DECIMALS = 2;

export interface StairTotals {
  /** Quantas escadas (un). */
  readonly count: number;
  /** A projeção em planta — lances e patamares — de cada escada, somada: o escoramento (m²). */
  readonly projection: number;
  /** A laje inclinada, os degraus e os patamares de cada escada, somados (m³). */
  readonly volume: number;
  /** Fundo inclinado, espelhos, laterais e fundo do patamar de cada escada, somados (m²). */
  readonly formwork: number;
  /** Quantos degraus, somados (un). */
  readonly steps: number;
}

/** Sem escada desenhada — o ponto de partida, e o que a tipologia sem escada devolve. */
export const NO_STAIRS: StairTotals = {
  count: 0,
  projection: 0,
  volume: 0,
  formwork: 0,
  steps: 0,
};

/**
 * A escada que conta: a que não foi excluída e tem ao menos um lance com contorno de verdade — o
 * mesmo critério do bloco. Lance ou patamar de contorno quebrado fica de fora da conta dela.
 */
function paraAConta(stair: Stair): Stair | undefined {
  if (stair.deletedAt !== undefined) return undefined;
  const flights = stair.flights.filter((lance) => isValidOutline(lance.outline));
  if (flights.length === 0) return undefined;
  const landings = stair.landings.filter((patamar) => isValidOutline(patamar.outline));
  return { ...stair, flights, landings };
}

/**
 * A conta das escadas de uma tipologia.
 *
 * Soma o que cada escada arredondou na casa dela, como o bloco: o total confere com a soma do que
 * a tela mostra de cada uma.
 */
export function stairTotals(stairs: readonly Stair[]): StairTotals {
  const contam = stairs.flatMap((stair) => paraAConta(stair) ?? []);
  const soma = (valores: readonly number[], casas: number): number =>
    roundQuantity(
      valores.reduce((total, valor) => total + valor, 0),
      casas,
    );
  return {
    count: contam.length,
    projection: soma(contam.map(stairProjection), STAIR_AREA_DECIMALS),
    volume: soma(contam.map(stairVolume), STAIR_VOLUME_DECIMALS),
    formwork: soma(contam.map(stairFormwork), STAIR_AREA_DECIMALS),
    steps: contam.reduce((total, stair) => total + stairSteps(stair), 0),
  };
}

/**
 * Os totais multiplicados pelos andares que repetem o desenho — a escada de cada um conta de
 * novo, como a laje, o bloco e a estaca (RN-167). Cada número volta à casa dele; as contagens são
 * inteiras.
 */
export function scaleStairTotals(totals: StairTotals, times: number): StairTotals {
  if (times === 1) return totals;
  const area = (valor: number): number => roundQuantity(valor * times, STAIR_AREA_DECIMALS);
  return {
    count: totals.count * times,
    projection: area(totals.projection),
    volume: roundQuantity(totals.volume * times, STAIR_VOLUME_DECIMALS),
    formwork: area(totals.formwork),
    steps: totals.steps * times,
  };
}
